import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import * as cssTable from '@delmonicos/shared-design-system-react/src/components/table/table.module.scss';
import { GatewayLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/locations/models/outputs/gateway-location.model';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';

import { LocationDeleteController } from '../location-delete/location-delete.controller';
import { LocationContextProvider } from '../location.context';

export interface LocationListActionsCellProps
  extends CellContext<GatewayLocationModel, unknown> {}

export const LocationListActionsCell = (
  props: LocationListActionsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canDelete =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.LOCATIONS,
      action: ActionsByResource.LOCATIONS.DELETE,
    });

  const canRead =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.LOCATIONS,
      action: ActionsByResource.LOCATIONS.READ,
    });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <LocationContextProvider data={row.original}>
      <Stack direction="row" justifyContent="flex-end">
        {canRead && (
          <ButtonIcon
            color={ButtonColor.Ghost}
            icon="eye"
            onClick={() =>
              navigate(
                generatePath(APP_PATH.LOCATION.DETAILS, {
                  id: row.original.id,
                }),
              )
            }
            className={cssTable.displayOnHover}
          />
        )}
        {canDelete && (
          <>
            <ButtonIcon
              color={ButtonColor.Ghost}
              icon="trash-01"
              onClick={() => setActionDeleteIsDialogOpen(true)}
              className={cssTable.displayOnHover}
            />
            <Dialog
              onClose={() => setActionDeleteIsDialogOpen(false)}
              open={!!actionDeleteIsDialogOpen}>
              <Dialog.Header
                title={t('$.location-delete.title')}
                icon="map-01"
                onClose={() => setActionDeleteIsDialogOpen(false)}
              />
              <Dialog.Content>
                <LocationDeleteController
                  onCancel={() => setActionDeleteIsDialogOpen(false)}
                  onSuccess={() => setActionDeleteIsDialogOpen(false)}
                />
              </Dialog.Content>
            </Dialog>
          </>
        )}
      </Stack>
    </LocationContextProvider>
  );
};
